import { siteMap } from "internal-sitemap";
import { useRef, useEffect } from "react";
import { useLocation } from "react-router";
import { useAuthentication } from "utilities/contexts/authentication/authentication-provider";
import {
    AuthenticationStrategyType,
    AuthenticationLifeCycleStatus,
} from "utilities/enumerations/authorization";
import LocalStorageKey from "utilities/enumerations/local-storage-keys";
import useFeatureFlags from "utilities/hooks/use-feature-flags";
import { LocalStorageUtils } from "utilities/local-storage-utils";
import { RouteUtils } from "utilities/route-utils";
import { useUrlCallback } from "utilities/routing/url-callback/use-url-callback";

export const useHandleUnauthorized = () => {
    const { authenticationLifeCycleStatus, login } = useAuthentication();
    const { setCallbackUrlParam } = useUrlCallback();
    const { enableUnauthorizedHandling, showSSODebugLogs } = useFeatureFlags();

    // Intentionally using local storage rather than globalState to avoid unnecessary re-renders.
    // This is only needed for unauthorized state handling and the initial value in globalState
    // comes from this value and will not get updated in the event of an unauthorized error.
    const authStrategyType =
        LocalStorageUtils.get<AuthenticationStrategyType>(
            LocalStorageKey.AuthStrategyType
        ) ?? AuthenticationStrategyType.AzureB2C;

    const attemptingLogin = useRef(false);
    const { pathname, search, hash } = useLocation();

    // It should be incredibly unlikely to get caught in a redirect loop after returning from the login page.
    // But just in case, we limit the number of redirects.
    const MAX_REDIRECTS = 2;
    const REDIRECT_COUNT_KEY = "unauthorizedRedirectCount";

    useEffect(() => {
        if (
            authenticationLifeCycleStatus !==
            AuthenticationLifeCycleStatus.Unauthorized
        ) {
            sessionStorage.removeItem(REDIRECT_COUNT_KEY);
            return;
        }

        if (!enableUnauthorizedHandling) {
            if (showSSODebugLogs) {
                console.log(
                    "Unauthorized handling is disabled via feature flag."
                );
            }

            return;
        }

        const redirectCount = parseInt(
            sessionStorage.getItem(REDIRECT_COUNT_KEY) ?? "0",
            10
        );

        if (redirectCount >= MAX_REDIRECTS) {
            if (showSSODebugLogs) {
                console.warn(
                    `Maximum unauthorized redirect attempts (${MAX_REDIRECTS}) reached. Stopping redirect.`
                );
            }

            // Trigger a reload and hope for the best
            window.location.href = RouteUtils.getUrl(siteMap.home);
            return;
        }

        const handleLogin = async () => {
            if (showSSODebugLogs) {
                console.log("***Handling Unauthorized - Redirecting to login");
            }

            // The landing page is the default so we don't need to add it as a callback
            // This also prevents creating a url with only an encoded "/": ?callback_url=%2F
            const isDefault = RouteUtils.assertCurrentUrl(siteMap.home);

            if (!isDefault) {
                const callbackLink = RouteUtils.getUrl(
                    pathname,
                    null,
                    search,
                    hash
                );

                setCallbackUrlParam(callbackLink);
            }

            sessionStorage.setItem(
                REDIRECT_COUNT_KEY,
                (redirectCount + 1).toString()
            );

            await login(authStrategyType);
            attemptingLogin.current = false;
        };

        if (!attemptingLogin.current) {
            attemptingLogin.current = true;
            handleLogin();
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authenticationLifeCycleStatus]);
};
